/**
 * Auth 模块（#89098）
 *
 * 数据面 `/.cloud/auth/v1/**`。
 *
 * **四个模块里唯一自研的**。Auth 也是唯一不能纯透传的能力：登录响应里 provider
 * 的 token 必须被换成我方自签 session，续期在服务端完成（§5.3 / §6.0）。
 *
 * ============================================================================
 * 本模块是整个客户端的**身份唯一来源**
 *
 * database / storage 的请求身份不由它们自己管 —— client.ts 把
 * `() => auth.getAccessToken()` 交给共享 fetch，于是登录一次其它模块自动带上
 * 身份，登出一次其它模块自动变匿名。调用方不需要（也不应该）手动搬 token。
 *
 * 这也意味着 `getAccessToken()` 在**每个** database/storage 请求前都会被调用、
 * 且可能被高并发调用 —— 它必须便宜且幂等。过期续期的并发保护在
 * session-manager.ts。
 * ============================================================================
 */

import type { CloudError, CloudResult } from '../../http/errors.js';
import { CLOUD_MODULE_PATHS } from '../../paths.js';
import type { CloudRuntimeConfig } from '../../types.js';
import {
    AUTH_PATHS,
    normalizeHttpError,
    normalizeNetworkError,
    parseSession,
    parseUser,
} from './protocol.js';
import { SessionManager } from './session-manager.js';
import { resolveStorage, SessionStore } from './session-store.js';
import type {
    CloudAuthEvent,
    CloudAuthStateCallback,
    CloudAuthStorage,
    CloudSession,
    CloudUser,
    OAuthRedirect,
    OAuthSignInOptions,
    OtpChallenge,
    OtpCredentials,
    OtpSignInChallenge,
    PasswordResetChallenge,
    PasswordCredentials,
    ResetPasswordWithOldCredentials,
    SignUpCredentials,
    VerifyOtpParams,
} from './types.js';

export interface AuthModuleOptions {
    /** 凭据存储。缺省用 localStorage（探测失败退回内存）。 */
    storage?: CloudAuthStorage;
}

export class AuthModule {
    /** 该模块的数据面基址。 */
    readonly baseUrl: string;

    private readonly sessions: SessionManager;
    private readonly storage: CloudAuthStorage;
    private readonly oauthRelayBaseUrl: string;
    private readonly listeners = new Map<number, CloudAuthStateCallback>();
    private nextListenerId = 1;

    /**
     * @param fetch 必须是**不带 session provider** 的 fetch。
     *
     * 原因：本模块的 `/v1/token` 就是续期端点；若它走「取 token（必要时先续期）」
     * 那条出口，续期就会调用自己 —— 死锁。CloudBase 的 OAuth2Client 为同一个
     * 问题留了逃生口，注释里直接写了 "may cause deadlock during initialization"。
     * 装配见 client.ts。
     */
    constructor(
        config: CloudRuntimeConfig,
        private readonly fetch: typeof globalThis.fetch,
        opts: AuthModuleOptions = {},
    ) {
        this.baseUrl = `${config.endpoint}${CLOUD_MODULE_PATHS.auth}`;
        this.oauthRelayBaseUrl = config.oauthRelayBaseUrl;
        this.storage = resolveStorage(opts.storage);
        this.sessions = new SessionManager({
            store: new SessionStore(this.storage, config.publishableKey),
            refresh: (handle) => this.exchangeRefreshHandle(handle),
            emit: (event, session) => this.notify(event, session),
            isCredentialsRejected: (err) => isCloudError(err) && rejectedCredentials(err),
        });
    }

    // ------------------------------------------------------------------
    // 供 client.ts 装配共享 fetch 用
    // ------------------------------------------------------------------

    /**
     * 取一个当前可用的 access token，必要时先续期。无会话返回 undefined。
     *
     * database / storage 的每个请求都会调它 —— 返回 undefined 表示匿名，
     * 那是合法状态不是错误（服务端按 anon 角色处理）。
     */
    async getAccessToken(): Promise<string | undefined> {
        const session = await this.sessions.ensure();
        return session?.accessToken;
    }

    // ------------------------------------------------------------------
    // 登录
    // ------------------------------------------------------------------

    /** 邮箱 + 密码登录。当前环境未启用手机号或匿名登录。 */
    async signInWithPassword(credentials: PasswordCredentials): Promise<CloudResult<CloudSession>> {
        if (!credentials.email || !credentials.password) {
            return fail(badRequest('signInWithPassword requires email and password'));
        }
        return this.postForSession(AUTH_PATHS.signIn, {
            username: credentials.email,
            password: credentials.password,
        });
    }

    /**
     * 小程序 wx.login 的 code 换 Genie session。
     * 试用与正式是两套独立小程序，必须带当前账号的 appid
     *（`wx.getAccountInfoSync().miniProgram.appId`）。
     */
    async signInWithWechat(code: string, appid: string): Promise<CloudResult<CloudSession>> {
        if (!code) {
            return fail(badRequest('signInWithWechat requires code'));
        }
        if (!appid) {
            return fail(badRequest('signInWithWechat requires appid'));
        }
        return this.postForSession(AUTH_PATHS.loginWechat, { code, appid });
    }

    /**
     * 注册。
     *
     * 上游要求先完成验证码验证，所以必须带一个 `verificationToken`（来自
     * `verifyOtp`）。只有用户名+密码的注册会被上游明确拒绝。
     *
     * 账号标识用 `email` 或 `phone`（二选一，与发码渠道一致）。
     */
    async signUp(
        credentials: SignUpCredentials & { verificationToken: string },
    ): Promise<CloudResult<CloudSession>> {
        if (!credentials.email && !credentials.phone) {
            return fail(badRequest('signUp requires email or phone'));
        }
        if (!credentials.verificationToken) {
            return fail(
                badRequest(
                    'signUp requires a verificationToken; run sendOtp + verifyOtp first ' +
                        '(the provider rejects username+password-only signup)',
                ),
            );
        }
        const body: Record<string, unknown> = {
            verification_token: credentials.verificationToken,
        };
        if (credentials.phone) body.phone_number = normalizePhone(credentials.phone);
        if (credentials.email) body.email = credentials.email;
        if (credentials.password) body.password = credentials.password;
        return this.postForSession(AUTH_PATHS.signUp, body);
    }

    /**
     * 发验证码（邮箱或短信，由 `credentials` 传的字段决定）。
     *
     * 这一步**不产生会话**，只返回一个 `verificationId`。发码由上游完成，
     * 我方不实现发码逻辑。
     *
     * 返回的 `isExistingUser` 决定验码之后走登录还是注册 —— 该判断由上游给出，
     * SDK 不猜。
     *
     * 两条渠道的请求体差异是刻意的（runtime-auth-design §3.1）：
     * - 邮箱：`{ email, usage: 'EMAIL' }`
     * - 手机：`{ phone_number, target: 'ANY' }` —— **不带 `usage`**（普通短信登录
     *   发码时省略即可，上游没有对应的短信枚举值），`target: 'ANY'` 表示新老用户
     *   都允许发码。
     */
    async sendOtp(credentials: OtpCredentials): Promise<CloudResult<OtpChallenge>> {
        const body: Record<string, unknown> = {};
        if (credentials.phone) {
            body.phone_number = normalizePhone(credentials.phone);
            body.target = 'ANY';
        } else if (credentials.email) {
            body.email = credentials.email;
            body.usage = 'EMAIL';
            if (credentials.emailRedirectTo) {
                body.email_redirect_to = credentials.emailRedirectTo;
            }
        } else {
            return fail(badRequest('sendOtp requires email or phone'));
        }

        const res = await this.request(AUTH_PATHS.verification, { method: 'POST', body });
        if (res.error) {
            return fail(res.error);
        }
        const payload = (res.data ?? {}) as Record<string, unknown>;
        const verificationId =
            typeof payload.verification_id === 'string' ? payload.verification_id : '';
        if (!verificationId) {
            return fail(badRequest('provider returned no verification_id'));
        }
        return ok({ verificationId, isExistingUser: payload.is_user === true });
    }

    /** gotrue 风格的 OTP 入口：发码后由 challenge.verify 完成登录。 */
    async signInWithOtp(
        credentials: OtpCredentials,
    ): Promise<CloudResult<OtpSignInChallenge>> {
        const sent = await this.sendOtp(credentials);
        if (sent.error) {
            return fail(sent.error);
        }
        const { verificationId, isExistingUser } = sent.data;
        const phone = credentials.phone ? normalizePhone(credentials.phone) : undefined;
        return ok({
            verificationId,
            isExistingUser,
            isUser: isExistingUser,
            verify: ({ token, password }) =>
                this.verifyOtp({
                    verificationId,
                    token,
                    email: credentials.email,
                    phone,
                    isExistingUser,
                    password,
                }),
        });
    }

    /**
     * 验码并登录（或注册后登录）。
     *
     * 两步：先用验证码换一个**一次性** `verification_token`，再用它换会话。
     * 中间那个 token 不是会话 —— 拿它当 Bearer 会被拒。
     *
     * 走登录还是注册由 `isExistingUser` 决定（`sendOtp` 的返回值）。刻意不做
     * 「先试登录失败再试注册」的兜底：那会把「密码错」这类真实错误掩盖成一次
     * 莫名的注册尝试。
     *
     * 登录侧邮箱与手机号共用 `username` 字段 —— 上游 `SignInRequest.username`
     * 承载邮箱/手机/用户名三种形态（runtime-auth-design §3.2）。
     */
    async verifyOtp(
        params: VerifyOtpParams & { isExistingUser: boolean },
    ): Promise<CloudResult<CloudSession>> {
        const identifier = params.phone ? normalizePhone(params.phone) : params.email ?? '';
        if (!params.verificationId || !params.token || !identifier) {
            return fail(
                badRequest('verifyOtp requires verificationId, token and either email or phone'),
            );
        }
        const verified = await this.request(AUTH_PATHS.verificationVerify, {
            method: 'POST',
            body: {
                verification_id: params.verificationId,
                verification_code: params.token,
            },
        });
        if (verified.error) {
            return fail(verified.error);
        }
        const vt = (verified.data ?? {}) as Record<string, unknown>;
        const verificationToken =
            typeof vt.verification_token === 'string' ? vt.verification_token : '';
        if (!verificationToken) {
            return fail(badRequest('provider returned no verification_token'));
        }

        if (params.isExistingUser) {
            return this.postForSession(AUTH_PATHS.signIn, {
                username: identifier,
                verification_token: verificationToken,
            });
        }
        return this.signUp({
            email: params.email,
            phone: params.phone,
            password: params.password ?? '',
            verificationToken,
        });
    }

    // ------------------------------------------------------------------
    // OAuth
    // ------------------------------------------------------------------

    /**
     * 发起第三方登录，返回该把用户送去的地址。
     *
     * 一期固定走 Cloud Backend Service OAuth Relay。IdP 回调白名单只登记平台
     * 统一域名，由 Relay 把结果转回具体应用；应用不需要理解 Provider 配置、
     * state 校验或回调白名单。
     *
     * SDK 不自己跳转（不写 `location.href`）：跳转时机应由应用决定 —— 有的要
     * 先存草稿，有的在 iframe 里要开弹窗。返回 url 交调用方处置。
     *
     * @deprecated Google 登录已下线，新应用不应再调用该方法。
     */
    async signInWithOAuth(options: OAuthSignInOptions): Promise<CloudResult<OAuthRedirect>> {
        if (!this.oauthRelayBaseUrl) {
            return fail(badRequest('signInWithOAuth requires oauthRelayBaseUrl'));
        }
        if (options.provider !== 'google') {
            return fail(badRequest('signInWithOAuth currently supports only google'));
        }
        const redirectTo = options.redirectTo ?? currentPageUrl();
        if (!redirectTo) {
            return fail(
                badRequest('signInWithOAuth requires redirectTo outside a browser environment'),
            );
        }
        return this.startRelayOAuth(options, redirectTo);
    }

    /**
     * 完成第三方登录：读回调 URL 上的参数换会话。
     *
     * 在回调落地页调一次即可。`code` 缺失时返回 `null` 数据而**不是**错误 ——
     * 落地页可能被直接访问（用户收藏了它），那不是失败。
     *
     * @deprecated Google 登录已下线，新应用不应再调用该方法。
     */
    async handleOAuthCallback(search?: string): Promise<CloudResult<CloudSession | null>> {
        const callbackInput = search ?? currentPageSearch();
        let query = callbackInput;
        try {
            if (/^https?:\/\//i.test(callbackInput)) {
                query = new URL(callbackInput).search;
            }
        } catch {
            return fail(badRequest('oauth callback URL is invalid'));
        }
        const params = new URLSearchParams(query);
        // 上游的失败也从 query 回来（用户点了「拒绝授权」）。
        const oauthError = params.get('error') ?? params.get('error_description');
        if (oauthError) {
            return fail({
                kind: 'unauthenticated',
                message: `oauth provider rejected the request: ${oauthError}`,
                status: 0,
            });
        }
        const code = params.get('code');
        if (!code) {
            return ok(null);
        }

        const state = params.get('state') ?? '';
        const providerId = params.get('provider_id');
        if (providerId !== 'custom-oauth') {
            return fail(badRequest('oauth callback requires provider_id=custom-oauth'));
        }
        if (!state) {
            return fail(badRequest('oauth callback requires state'));
        }

        const body: Record<string, unknown> = {
            provider_id: providerId,
            provider_code: code,
            state,
            provider_params: { state },
        };
        return this.postForSession(AUTH_PATHS.signInWithProvider, body);
    }

    /** 忘记密码：发码，验证码通过后更新密码并自动登录。 */
    async resetPasswordForEmail(
        email: string,
    ): Promise<CloudResult<PasswordResetChallenge>> {
        if (!email) {
            return fail(badRequest('resetPasswordForEmail requires email'));
        }
        const sent = await this.request(AUTH_PATHS.verification, {
            method: 'POST',
            body: { email, usage: 'PASSWORD_RESET', target: 'USER' },
        });
        if (sent.error) {
            return fail(sent.error);
        }
        const payload = (sent.data ?? {}) as Record<string, unknown>;
        const verificationId =
            typeof payload.verification_id === 'string' ? payload.verification_id : '';
        if (!verificationId) {
            return fail(badRequest('provider returned no verification_id'));
        }
        return ok({
            updateUser: async ({ nonce, password }) => {
                const verified = await this.request(AUTH_PATHS.verificationVerify, {
                    method: 'POST',
                    body: {
                        verification_id: verificationId,
                        verification_code: nonce,
                    },
                });
                if (verified.error) {
                    return fail(verified.error);
                }
                const verifiedPayload = (verified.data ?? {}) as Record<string, unknown>;
                const verificationToken =
                    typeof verifiedPayload.verification_token === 'string'
                        ? verifiedPayload.verification_token
                        : '';
                if (!verificationToken) {
                    return fail(badRequest('provider returned no verification_token'));
                }
                const reset = await this.request(AUTH_PATHS.resetPassword, {
                    method: 'POST',
                    body: {
                        email,
                        new_password: password,
                        verification_token: verificationToken,
                    },
                });
                if (reset.error) {
                    return fail(reset.error);
                }
                const signedIn = await this.signInWithPassword({ email, password });
                if (!signedIn.error) {
                    this.notify('PASSWORD_RECOVERY', signedIn.data);
                }
                return signedIn;
            },
        });
    }

    /** 登录后使用旧密码进行 sudo 校验并更新密码。 */
    async resetPasswordForOld(
        credentials: ResetPasswordWithOldCredentials,
    ): Promise<CloudResult<CloudSession>> {
        const token = await this.getAccessToken();
        if (!token) {
            return fail({ kind: 'unauthenticated', message: 'no active session', status: 0 });
        }
        const sudo = await this.request(AUTH_PATHS.sudo, {
            method: 'POST',
            token,
            body: { password: credentials.oldPassword },
        });
        if (sudo.error) {
            return fail(sudo.error);
        }
        const payload = (sudo.data ?? {}) as Record<string, unknown>;
        const sudoToken = typeof payload.sudo_token === 'string' ? payload.sudo_token : '';
        if (!sudoToken) {
            return fail(badRequest('provider returned no sudo_token'));
        }
        const updated = await this.request(AUTH_PATHS.userPassword, {
            method: 'PATCH',
            token,
            body: { sudo_token: sudoToken, new_password: credentials.newPassword },
        });
        if (updated.error) {
            return fail(updated.error);
        }
        const session = await this.getSession();
        if (session.data) {
            this.notify('USER_UPDATED', session.data);
            return ok(session.data);
        }
        return fail(session.error ?? {
            kind: 'unauthenticated',
            message: 'no active session',
            status: 0,
        });
    }

    // ------------------------------------------------------------------
    // 会话
    // ------------------------------------------------------------------

    /**
     * 读当前会话。
     *
     * 会在临近过期时**顺手续期** —— 只读本地不续期的话，调用方拿到一个还剩
     * 3 秒有效期的 token 去发请求，到服务端就已经过期了。
     */
    async getSession(): Promise<CloudResult<CloudSession | null>> {
        try {
            return ok(await this.sessions.ensure());
        } catch (err) {
            return fail(isCloudError(err) ? err : normalizeNetworkError(err));
        }
    }

    /** 强制续期一次，不管是否临近过期。 */
    async refreshSession(): Promise<CloudResult<CloudSession | null>> {
        try {
            return ok(await this.sessions.forceRefresh());
        } catch (err) {
            return fail(isCloudError(err) ? err : normalizeNetworkError(err));
        }
    }

    /**
     * 读当前用户。
     *
     * 会发请求（`/v1/user/me`）而不是读本地 session —— 后者的 user 字段来自
     * 登录那一刻，可能已过时（别处改了昵称、或匿名已升级为实名）。
     */
    async getUser(): Promise<CloudResult<CloudUser>> {
        const token = await this.getAccessToken();
        if (!token) {
            return fail({ kind: 'unauthenticated', message: 'no active session', status: 0 });
        }
        const res = await this.request(AUTH_PATHS.userMe, { method: 'GET', token });
        if (res.error) {
            // 会话在服务端已失效（例如别处登出了）：本地也要清，否则界面会一直
            // 显示登录态却什么都做不了。
            if (rejectedCredentials(res.error)) {
                this.sessions.clear();
            }
            return fail(res.error);
        }
        return ok(parseUser(res.data));
    }

    /**
     * 登出。
     *
     * 无论服务端返回什么都清本地：服务端侧是**无条件销毁**的（转发上游之后就
     * 销毁，不看上游成败），本地留着只会让界面显示一个已经不能用的登录态。
     * 断网时也必须能登出。
     */
    async signOut(): Promise<CloudResult<null>> {
        const session = this.sessions.peek();
        if (session?.accessToken) {
            // 必须带 Authorization：不带的话服务端不知道要销毁哪个会话，
            // 会静默什么都不做。
            await this.request(AUTH_PATHS.signOut, {
                method: 'POST',
                body: {},
                token: session.accessToken,
            });
        }
        this.sessions.clear();
        return ok(null);
    }

    /**
     * 订阅登录态变化。返回取消订阅函数。
     *
     * 订阅时会**立即**回放一次 `INITIAL_SESSION`：没有它，调用方无法区分
     * 「还没初始化完」与「初始化完了但未登录」，首屏会闪一下登录页。
     */
    onAuthStateChange(callback: CloudAuthStateCallback): () => void {
        const id = this.nextListenerId++;
        this.listeners.set(id, callback);
        // 同步回放，不推到微任务：调用方常在首次 render 前订阅，延迟一拍就会
        // 多渲染一帧未登录状态。
        safeInvoke(callback, 'INITIAL_SESSION', this.sessions.peek());
        return () => void this.listeners.delete(id);
    }

    // ------------------------------------------------------------------
    // 内部
    // ------------------------------------------------------------------

    /**
     * 拼出平台 Relay 授权地址；state 由 Relay 自己签发、校验并一次性消费。
     *
     * @deprecated Google 登录已下线，仅保留给存量应用的回调落地页。
     */
    private async startRelayOAuth(
        options: OAuthSignInOptions,
        redirectTo: string,
    ): Promise<CloudResult<OAuthRedirect>> {
        // Cloud Backend Relay 自己签发、校验并一次性消费 state；应用只提供最终
        // callback URL，不能自行生成或覆盖 state。
        const callback = new URL(redirectTo);
        const relay = new URL(`${this.oauthRelayBaseUrl}/authorize`);
        relay.searchParams.set('provider', options.provider);
        relay.searchParams.set('callback_url', callback.toString());

        return ok({ url: relay.toString() });
    }

    /** 用我方句柄换新会话。这是 SessionManager 注入的续期执行体。 */
    private async exchangeRefreshHandle(handle: string): Promise<CloudSession> {
        // 刻意**不带** Authorization：句柄本身就是凭据；带上一个已过期的
        // access token 反而会在服务端的鉴权阶段先被 401 挡掉。
        const res = await this.request(AUTH_PATHS.token, {
            method: 'POST',
            body: { grant_type: 'refresh_token', refresh_token: handle },
        });
        if (res.error) {
            throw res.error;
        }
        return parseSession(res.data, Date.now());
    }

    /** POST 一个换签端点并把响应解析成会话。 */
    private async postForSession(
        path: string,
        body: unknown,
        opts: { headers?: Record<string, string> } = {},
    ): Promise<CloudResult<CloudSession>> {
        const res = await this.request(path, { method: 'POST', body, headers: opts.headers });
        if (res.error) {
            return fail(res.error);
        }
        try {
            return ok(this.sessions.commitSignIn(parseSession(res.data, Date.now())));
        } catch (err) {
            return fail(isCloudError(err) ? err : normalizeNetworkError(err));
        }
    }

    /** 发一次 auth 请求并归一错误。 */
    private async request(
        path: string,
        init: {
            method: string;
            body?: unknown;
            token?: string;
            headers?: Record<string, string>;
        },
    ): Promise<CloudResult<unknown>> {
        const headers: Record<string, string> = { ...init.headers };
        if (init.body !== undefined) {
            // Content-Type 必须是 JSON：服务端只在这个前提下才解析 body
            // （它要从里面找 refresh_token / redirect_uri 等字段）。
            headers['Content-Type'] = 'application/json';
        }
        if (init.token) {
            headers.Authorization = `Bearer ${init.token}`;
        }

        let response: Response;
        try {
            response = await this.fetch(this.baseUrl + path, {
                method: init.method,
                headers,
                body: init.body === undefined ? undefined : JSON.stringify(init.body),
            });
        } catch (err) {
            // fetch 层失败。403/404 在浏览器里也会走到这里（服务端对这两类刻意
            // 不回 CORS 头），所以这条分支不是「只有断网才进」。
            return fail(normalizeNetworkError(err));
        }

        const payload = await readBody(response);
        if (!response.ok) {
            return fail(normalizeHttpError(response.status, payload));
        }
        return ok(payload);
    }

    /** 广播事件。单个监听器抛错不能影响其它监听器与主流程。 */
    private notify(event: CloudAuthEvent, session: CloudSession | null): void {
        for (const cb of this.listeners.values()) {
            safeInvoke(cb, event, session);
        }
    }
}

// ----------------------------------------------------------------------
// 小工具
// ----------------------------------------------------------------------

function ok<T>(data: T): CloudResult<T> {
    return { data, error: null };
}

function fail<T>(error: CloudError): CloudResult<T> {
    return { data: null, error };
}

function badRequest(message: string): CloudError {
    return { kind: 'invalid-request', message, status: 0 };
}

/**
 * 归一化国内手机号为 `+86 13800000000` 形式（+86 后带一个空格）。
 *
 * 兼容输入：裸号（`13800000000`）、带区号但无空格（`+8613800000000`、
 * `8613800000000`）、国际冠码（`0086...`）、各种分隔符（空格/连字符）。
 * 非 11 位国内手机号（如港澳号 `+852...`）原样返回，不破坏非大陆号码。
 */
function normalizePhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    let local = digits;
    if (local.startsWith('0086')) {
        local = local.slice(4);
    } else if (local.startsWith('86') && local.length === 13) {
        local = local.slice(2);
    }
    return /^1\d{10}$/.test(local) ? `+86 ${local}` : phone;
}

function isCloudError(v: unknown): v is CloudError {
    return !!v && typeof v === 'object' && typeof (v as CloudError).kind === 'string';
}

/** 该错误是否意味着本地凭据已废。 */
function rejectedCredentials(error: CloudError): boolean {
    return error.kind === 'unauthenticated' || error.code === 'invalid_grant';
}

async function readBody(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch {
        // 非 JSON 响应（网关错误页等）：包一层，让上层仍能拿到原文。
        return { message: text };
    }
}

function safeInvoke(
    cb: CloudAuthStateCallback,
    event: CloudAuthEvent,
    session: CloudSession | null,
): void {
    try {
        cb(event, session);
    } catch {
        // 监听器里的异常是调用方的问题，不能让它中断登录/登出流程。
    }
}

/** 当前页地址（去掉 query 与 hash）—— OAuth 回调的缺省落地点。 */
function currentPageUrl(): string {
    const loc = globalThis.location;
    return loc ? loc.origin + loc.pathname : '';
}

function currentPageSearch(): string {
    return globalThis.location?.search ?? '';
}
